// imports trimmed after helper extraction
import type { DocumentError } from 'apiConnector/document/document';
import { LanguageDownload } from 'components/shared/Document/MultiLanguageDocument';
import type { ErrorDisplayContext } from 'components/shared/Document/MultiLanguageDocumentItem';
import { DocumentFormat } from 'epic/common-form/Documentation';
import { DocumentType } from 'models/documents/DocumentType';
import { connect } from 'react-redux';
import { documentDownloaded, documentDownloadFailed, documentDownloadStart } from 'state/document/actions';
import { LoadableResultByLanguage } from 'state/document/model';
import { hasErrorForType } from 'state/document/selector';
import {
  getDocument,
  getFormInfos,
  getFormProductSubType,
  getFormProductType,
  getIsUserInternal,
  isPendingEnrichment,
} from 'state/selectors';
import { getQuoteExternalIdByWorkspaceTab } from 'state/selectors/common/temp';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import { buildLanguages } from '../documents/DocumentButton.connect.helper';

interface Language extends LanguageDownload {
  fileName?: string;
}
interface OwnProps {
  className?: string;
  tabId: string;
  documentType: DocumentType;
  workspaceType: QuoteWorkspaceType;
}

interface StateProps {
  languages: Language[];
  isEnabled: boolean;
  errors?: Partial<LoadableResultByLanguage>;
  errorContext?: ErrorDisplayContext;
  quoteQuery: unknown;
  productSubtype?: string;
  quoteId?: string;
  quoteState?: string;
  productType?: string;
}

interface DispatchProps {
  onError: (locale: string, error: DocumentError, documentFormat: DocumentFormat) => void;
  onDownloadStart: (locale: string, documentFormat: DocumentFormat) => void;
  onSuccess: (locale: string, documentFormat: DocumentFormat) => void;
}
export type DocumentProps = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = (state: AppState, { tabId, workspaceType, documentType }: OwnProps): StateProps => {
  const infos = getFormInfos(state, workspaceType, tabId);
  const isInternal = getIsUserInternal(state);

  const documentation = getDocument(state, workspaceType, tabId, documentType);

  const productSubtype = getFormProductSubType(state, tabId);
  const productType = getFormProductType(state, tabId);
  const quoteId = getQuoteExternalIdByWorkspaceTab(state, workspaceType, tabId);
  const pendingEnrichment = isPendingEnrichment(state, tabId);
  const errorContext = hasErrorForType(state, tabId, documentType)
    ? {
        productSubtype,
        shouldDisplayErrorDetails: isInternal,
        shouldDisplayStandardErrorMessage: !isInternal,
        shouldUseRejectionCode: !isInternal,
      }
    : undefined;

  return {
    languages: buildLanguages({
      state,
      tabId,
      documentType,
      documentation,
    }),
    isEnabled: !pendingEnrichment,
    quoteQuery: infos?.quoteQuery,
    errorContext,
    productSubtype,
    quoteId,
    quoteState: 'Indicative',
    productType,
  };
};

export function mapDispatchToProps(dispatch: ThunkDispatch, { tabId, documentType }: OwnProps): DispatchProps {
  return {
    onError(locale: string, error: DocumentError, documentFormat: DocumentFormat) {
      dispatch(documentDownloadFailed(tabId, locale, error, documentType, documentFormat));
    },
    onSuccess(locale: string, documentFormat: DocumentFormat) {
      dispatch(documentDownloaded(tabId, locale, documentType, documentFormat));
    },
    onDownloadStart(locale: string, documentFormat: DocumentFormat) {
      dispatch(documentDownloadStart(tabId, locale, documentType, documentFormat));
    },
  };
}

export const connectDocument = connect(mapStateToProps, mapDispatchToProps);
